import { z } from 'zod';
import { kePhoneSchema } from './common.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';

export const EMPLOYMENT_STATUSES = ['active', 'suspended', 'left'] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUSES)[number];

/** `POST /drivers` and `PATCH /drivers/:id` — employment status changes go through their own `POST /drivers/:id/status`. */
export const createDriverSchema = z.object({
  name: z.string().trim().min(1).max(160),
  phone: kePhoneSchema,
  nationalId: z.string().trim().min(1).max(40),
  dateOfBirth: z.iso.date(),
  licenceNumber: z.string().trim().min(1).max(40),
  licenceClasses: z.array(z.string().trim().min(1).max(10)).min(1),
  hazmatCertified: z.boolean(),
});
export type CreateDriverInput = z.infer<typeof createDriverSchema>;

export const updateDriverSchema = createDriverSchema;
export type UpdateDriverInput = z.infer<typeof updateDriverSchema>;

/** `POST /drivers/:id/status` — one endpoint for every transition; nothing restricts which are legal. */
export const driverStatusChangeSchema = z.object({
  status: z.enum(EMPLOYMENT_STATUSES),
  effectiveAt: z.iso.date(),
  reason: z.string().trim().min(1).max(500).optional(),
});
export type DriverStatusChangeInput = z.infer<typeof driverStatusChangeSchema>;

/** `GET /drivers` — search matches name, phone and licence number, not `nationalId` (CLAUDE.md → Data protection). */
export const driverListQuerySchema = defineListQuery({
  sortFields: ['name', 'createdAt'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: {
    status: listFilter(z.enum(EMPLOYMENT_STATUSES)),
  },
});
export type DriverListQuery = z.infer<typeof driverListQuerySchema>;

export const DRIVER_DOCUMENT_CONTENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;
export type DriverDocumentContentType = (typeof DRIVER_DOCUMENT_CONTENT_TYPES)[number];

/** 10 MB — nothing sizes this elsewhere; the org logo's 512 KB is for a UI asset, not a legal-hard-block compliance document. */
export const DRIVER_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

/** `POST /drivers/:id/documents/upload-url` — presigns straight to object storage (CLAUDE.md: large files go by presigned URL). */
export const driverDocumentUploadUrlSchema = z.object({
  documentTypeCode: z.string().trim().min(1).max(40),
  contentType: z.enum(DRIVER_DOCUMENT_CONTENT_TYPES),
});
export type DriverDocumentUploadUrlInput = z.infer<typeof driverDocumentUploadUrlSchema>;

/** `POST /drivers/:id/documents` — confirms the presigned upload actually landed before the row is written. */
export const driverDocumentAttachSchema = z.object({
  documentTypeCode: z.string().trim().min(1).max(40),
  key: z.string().trim().min(1),
  contentType: z.enum(DRIVER_DOCUMENT_CONTENT_TYPES),
  issuedAt: z.iso.date().optional(),
  expiresAt: z.iso.date().optional(),
});
export type DriverDocumentAttachInput = z.infer<typeof driverDocumentAttachSchema>;
